import {useCallback, useEffect, useMemo, useRef} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useForm} from 'react-hook-form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {createInitialFormStateV2} from 'src/types/FormV2Mapping';
import {createProactResolver} from './resolver';
import type {FieldsByInvariantId, VisibilityMap} from './visibilityEvaluator';
import {
  buildFieldsByInvariantId,
  evaluateVisibilityMap,
} from './visibilityEvaluator';

export interface UseProactFormOptions {
  formMeta: IFormFieldV2[];
  /** Initial values merged over metadata defaults. */
  initialData?: IFormDataV2;
  /** Initial display values (defaults to the raw values). */
  initialDisplayData?: IFormDataV2;
  /**
   * When true (default), fields validate as the user edits before the first
   * submit attempt. When false, validation only starts at the first submit.
   */
  enablePreSubmissionValidation?: boolean;
}

export interface SubmissionEntry {
  name: string;
  field: IFormFieldV2;
  value: any;
  displayValue: any;
  visible: boolean;
}

export interface ProactFormApi {
  methods: UseFormReturn<IFormDataV2>;
  formMeta: IFormFieldV2[];
  fieldMap: Record<string, IFormFieldV2>;
  fieldsByInvariantId: FieldsByInvariantId;
  /** Records a display value for a field (called by field adapters). */
  setDisplayValue: (name: string, displayValue: any) => void;
  /** Programmatic value write; marks the form dirty. */
  setFieldValue: (name: string, value: any, displayValue?: any) => void;
  getDisplayValues: () => IFormDataV2;
  /**
   * Values for exactly the metadata fields. Unlike methods.getValues(), this
   * excludes extraneous keys picked up from entity-shaped resets — required
   * when merging multiple section forms that were seeded from one entity.
   */
  getFieldValues: () => IFormDataV2;
  /** Visibility map evaluated against the current values. */
  getVisibility: () => VisibilityMap;
  /**
   * One entry per metadata field with its value, display value and current
   * visibility — the payload source for submission mapping.
   */
  getSubmissionEntries: () => SubmissionEntry[];
  /** Replaces values (and display values) and clears dirty/error state. */
  resetForm: (data?: IFormDataV2, displayData?: IFormDataV2) => void;
  /** Validates all visible fields; returns whether the form is valid. */
  validateForm: () => Promise<boolean>;
}

/**
 * Merges `overrides` over `base`, skipping any key whose override value is
 * `undefined` — an explicit `undefined` in initialData must not clobber a
 * metadata default (e.g. `{...content.lesson, timeLimit: content.timeLimit}`
 * when `content.timeLimit` is unset). `null` is a real value and still wins.
 */
function mergeDefined(base: IFormDataV2, overrides?: IFormDataV2): IFormDataV2 {
  if (!overrides) return {...base};
  const merged = {...base};
  for (const key of Object.keys(overrides)) {
    if (overrides[key] !== undefined) {
      merged[key] = overrides[key];
    }
  }
  return merged;
}

export function useProactForm({
  formMeta,
  initialData,
  initialDisplayData,
  enablePreSubmissionValidation = true,
}: UseProactFormOptions): ProactFormApi {
  const resolver = useMemo(() => createProactResolver(formMeta), [formMeta]);

  const defaultValues = useMemo(
    () => mergeDefined(createInitialFormStateV2(formMeta), initialData),
    // Recomputing on data/meta arrival is intentional; values are applied via
    // resetForm, defaultValues only seeds the first render.
    [formMeta, initialData]
  );

  const methods = useForm<IFormDataV2>({
    defaultValues,
    resolver,
    mode: enablePreSubmissionValidation ? 'onChange' : 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: false,
  });

  const displayValuesRef = useRef<IFormDataV2>({...initialDisplayData});

  // Metadata often arrives after mount (option queries resolving), so the
  // defaults seeded at useForm() time were computed from an empty meta array.
  // Reseed whenever the metadata changes, keeping any value that is already
  // set — API-loaded resets and user edits win over metadata defaults.
  // Keyed on the parts of the metadata that drive default values rather than
  // on array identity: consumers may build the meta array inline each render,
  // and resetting per render would loop.
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const formMetaRef = useRef(formMeta);
  formMetaRef.current = formMeta;
  const metaSeedKey = useMemo(
    () =>
      formMeta
        .map(
          field =>
            `${field.name}:${field.type}:${
              field.builderOptions?.defaultValue ?? ''
            }`
        )
        .join('|'),
    [formMeta]
  );
  const seededKeyRef = useRef(metaSeedKey);
  useEffect(() => {
    if (metaSeedKey === seededKeyRef.current || metaSeedKey === '') {
      return;
    }
    seededKeyRef.current = metaSeedKey;
    const merged: IFormDataV2 = mergeDefined(
      createInitialFormStateV2(formMetaRef.current),
      initialDataRef.current
    );
    const current = methods.getValues();
    Object.keys(current).forEach(name => {
      if (current[name] !== undefined) {
        merged[name] = current[name];
      }
    });
    methods.reset(merged, {
      keepDirty: true,
      keepDirtyValues: true,
      keepErrors: true,
      keepTouched: true,
      keepIsSubmitted: true,
      keepSubmitCount: true,
    });
  }, [metaSeedKey, methods]);

  const fieldMap = useMemo(() => {
    const map: Record<string, IFormFieldV2> = {};
    formMeta.forEach(field => {
      map[field.name] = field;
    });
    return map;
  }, [formMeta]);

  const fieldsByInvariantId = useMemo(
    () => buildFieldsByInvariantId(formMeta),
    [formMeta]
  );
  // API contract: every function on ProactFormApi is identity-stable for the
  // life of the form (consumers put them in effect deps; meta may be rebuilt
  // inline each render), so functions read meta through refs. Data fields
  // (formMeta, fieldMap, fieldsByInvariantId) stay reactive for rendering.
  const fieldsByInvariantIdRef = useRef(fieldsByInvariantId);
  fieldsByInvariantIdRef.current = fieldsByInvariantId;

  const setDisplayValue = useCallback((name: string, displayValue: any) => {
    displayValuesRef.current[name] = displayValue;
  }, []);

  const setFieldValue = useCallback(
    (name: string, value: any, displayValue?: any) => {
      methods.setValue(name, value, {shouldDirty: true, shouldTouch: true});
      displayValuesRef.current[name] = displayValue ?? value;
    },
    [methods]
  );

  const getDisplayValues = useCallback((): IFormDataV2 => {
    const values = methods.getValues();
    const displayValues: IFormDataV2 = {};
    Object.keys(values).forEach(name => {
      displayValues[name] = displayValuesRef.current[name] ?? values[name];
    });
    return displayValues;
  }, [methods]);

  const getFieldValues = useCallback((): IFormDataV2 => {
    const values = methods.getValues();
    const fieldValues: IFormDataV2 = {};
    formMetaRef.current.forEach(field => {
      fieldValues[field.name] = values[field.name];
    });
    return fieldValues;
  }, [methods]);

  const getVisibility = useCallback(
    () =>
      evaluateVisibilityMap(
        formMetaRef.current,
        methods.getValues(),
        fieldsByInvariantIdRef.current
      ),
    [methods]
  );

  const getSubmissionEntries = useCallback((): SubmissionEntry[] => {
    const values = methods.getValues();
    const visibility = evaluateVisibilityMap(
      formMetaRef.current,
      values,
      fieldsByInvariantIdRef.current
    );
    return formMetaRef.current.map(field => ({
      name: field.name,
      field,
      value: values[field.name],
      displayValue: displayValuesRef.current[field.name] ?? values[field.name],
      visible: visibility[field.name],
    }));
  }, [methods]);

  const resetForm = useCallback(
    (data?: IFormDataV2, displayData?: IFormDataV2) => {
      const nextValues = mergeDefined(
        createInitialFormStateV2(formMetaRef.current),
        data
      );
      displayValuesRef.current = {...displayData};
      methods.reset(nextValues);
    },
    [methods]
  );

  const validateForm = useCallback(() => methods.trigger(), [methods]);

  return useMemo(
    () => ({
      methods,
      formMeta,
      fieldMap,
      fieldsByInvariantId,
      setDisplayValue,
      setFieldValue,
      getDisplayValues,
      getFieldValues,
      getVisibility,
      getSubmissionEntries,
      resetForm,
      validateForm,
    }),
    [
      methods,
      formMeta,
      fieldMap,
      fieldsByInvariantId,
      setDisplayValue,
      setFieldValue,
      getDisplayValues,
      getFieldValues,
      getVisibility,
      getSubmissionEntries,
      resetForm,
      validateForm,
    ]
  );
}
